import React from 'react';
import { ModalBackground, ModalContent, ModalFooter, ModalLayout, ModalTitle } from 'components/Modal/ModalForms';
import BasicButton from 'components/Buttons/BasicButton';
import styled from 'styled-components';
import { AgeAndBirthDateWrapper, CloseButtonWrapper, TextInDetailPatientPageModal } from 'styles/Modal/modalStyle';
import { BackgroundColor, BorderColor } from 'styles/Global/colorSemantic';
import { Radius } from 'styles/Global/objectStyles';
import { Body3Medium } from 'styles/Global/typography';
import ModalDeleteIcon from 'assets/Icons/DeleteIcon/CloseDialogue';
import GenderMan from 'assets/Icons/Gender/GenderMan';
import GenderWoman from 'assets/Icons/Gender/GenderWoman';
import Account from 'assets/Icons/Account/Account';
import Noti from 'assets/Icons/Notice/Noti';
import { calculateBirthdayAge } from 'utils/calculateAge';
import { ICLUserInfo } from 'models/CL/CLBasicInterface';

interface MessageDeliverConfirmModalProps {
    toggle: () => void;
    userInfo: Pick<ICLUserInfo, 'name' | 'gender' | 'birthday'>;
    message: string;
    handleSendMessage: () => void;
}

const MessageDeliverConfirmModal = ({ toggle, userInfo, message, handleSendMessage }: MessageDeliverConfirmModalProps) => {
    const { name, gender, birthday } = userInfo;

    return (
        <ModalBackground>
            <ModalLayout $width='520px'>
                <ModalInnerLayout>
                    <ModalTitleAndContentContainer>
                        <FlexWrapper>
                            <Noti />
                            <ModalTitle>이대로 전송할까요?</ModalTitle>
                        </FlexWrapper>
                        <ColumnWrapper>
                            <ModalContent>전송하기를 클릭하면, 환자에게 작성하신 내용이</ModalContent>
                            <ModalContent>앱 알림 메시지로 바로 전송됩니다.</ModalContent>
                        </ColumnWrapper>
                    </ModalTitleAndContentContainer>
                    <UserInfoContainer>
                        <Account />
                        <InfoWrapper>
                            <TextInDetailPatientPageModal>{name || '미입력'}</TextInDetailPatientPageModal>
                            <TextInDetailPatientPageModal>
                                <span style={{ display: 'flex', alignItems: 'center' }}>{gender === 'male' ? <GenderMan /> : <GenderWoman />}</span>
                            </TextInDetailPatientPageModal>
                            <AgeAndBirthDateWrapper>
                                <span>{birthday ? calculateBirthdayAge(birthday).ageString : '미입력'}</span>
                                {birthday && <span>({calculateBirthdayAge(birthday).formattedBirthday})</span>}
                            </AgeAndBirthDateWrapper>
                        </InfoWrapper>
                    </UserInfoContainer>
                    <MessageContainer>{message}</MessageContainer>
                </ModalInnerLayout>
                <ModalFooter>
                    <BasicButton $width='160px' $height='48px' onClick={toggle} $type='anotherFill'>
                        취소
                    </BasicButton>
                    <BasicButton $width='160px' $height='48px' $type='fill' onClick={() => handleSendMessage()}>
                        전송하기
                    </BasicButton>
                </ModalFooter>
                <CloseButtonWrapper onClick={toggle}>
                    <ModalDeleteIcon />
                </CloseButtonWrapper>
            </ModalLayout>
        </ModalBackground>
    );
};

export default MessageDeliverConfirmModal;

const ModalInnerLayout = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    gap: 16px;
`;

const ModalTitleAndContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 12px;
`;

const UserInfoContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    padding: 18px 20px;
    border: 1px solid ${BorderColor.color_border_neutral};
    border-radius: 4px;
    gap: 8px;
`;

const MessageContainer = styled(Body3Medium)`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 18px 20px;
    border: 1px solid ${BorderColor.color_border_normal};
    background-color: ${BackgroundColor.color_background_disabled};
    border-radius: ${Radius.radius_4};
    word-wrap: break-word;
    word-break: break-all;
    white-space: pre-wrap;
    min-height: fit-content;
`;

const InfoWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
`;

const FlexWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ColumnWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
`;
